import { Injectable } from '@angular/core';
import { MapCoordinates, PointMarkerOptions, VehicleMarkerOptions } from '../models/tracking-map-base.model';
import { MapTrackingActionTypeNames, TransportationTrackingActionType } from '../../transportation-tracking';
import { TransportMode } from '../../../common/constants';
import { Coordinates, MapPoint, MapStopActionType } from '../../../common/models';
import { MapPolyline } from '../models/tracking-map.model';
import { uniq } from 'lodash';

@Injectable({ providedIn: 'root' })
export class TrackingMapsService {
  getVehicleMarkerOptions(
    vehiclePoint: Coordinates,
    transportMode: TransportMode,
    options?: { height: number; width: number },
  ): VehicleMarkerOptions | null {
    transportMode = transportMode || TransportMode.Road;

    if (!vehiclePoint) {
      return null;
    }

    return new VehicleMarkerOptions(vehiclePoint, transportMode, options);
  }

  getPointsMarkerOptions(points: MapPoint[]): PointMarkerOptions[] {
    return [...points]
      .sort((aPoint: MapPoint, bPoint: MapPoint) => {
        if (aPoint.sortIndex && bPoint?.sortIndex) {
          return aPoint.sortIndex.localeCompare(bPoint?.sortIndex, [], { numeric: true });
        }

        return aPoint?.dateTime?.timestamp - bPoint?.dateTime?.timestamp;
      })
      .map((point: MapPoint) => new PointMarkerOptions(point));
  }

  getPointsPolylineOptions(points: MapPoint[]): MapPolyline {
    return {
      ...new MapPolyline(),
      path: points.map((point: MapPoint) => {
        return new MapCoordinates(point);
      }),
    };
  }

  getMergedPoints<PointType extends MapPoint>(points: PointType[]): PointType[] {
    return Array.from(
      [...points]
        .sort((aPoint: PointType, bPoint: PointType) => {
          if (aPoint.sortIndex && bPoint?.sortIndex) {
            return aPoint.sortIndex.localeCompare(bPoint?.sortIndex, [], { numeric: true });
          }

          return aPoint?.dateTime?.timestamp - bPoint?.dateTime?.timestamp;
        })
        .reduce<Map<string, PointType>>((acc: Map<string, PointType>, point: PointType) => {
          if (acc.has(`${point.longitude}${point.latitude}`)) {
            return acc;
          }
          return acc.set(`${point.longitude}${point.latitude}`, point);
        }, new Map())
        .values(),
    );
  }

  getBounds(coordinates: Coordinates[]): google.maps.LatLngBounds {
    const mapCoordinates = coordinates.map((item: Coordinates) => new MapCoordinates(item));

    const bounds = new google.maps.LatLngBounds();
    mapCoordinates.forEach((item: MapCoordinates) => {
      bounds.extend(item);
    });

    return bounds;
  }

  getActionNames(actions: MapStopActionType[]): string[] {
    const isOnlyOneAction = actions.length === 1;
    return uniq(
      actions
        .map((item: MapStopActionType) => {
          if (
            item.action === TransportationTrackingActionType.Pickup ||
            item.action === TransportationTrackingActionType.Delivery
          ) {
            return item.action;
          }
          if (item.action === TransportationTrackingActionType.PortOfDischarge && isOnlyOneAction) {
            return MapTrackingActionTypeNames.Discharge;
          }
          if (item.action === TransportationTrackingActionType.PortOfLoading && isOnlyOneAction) {
            return MapTrackingActionTypeNames.Loading;
          }
          if (
            (item.action === TransportationTrackingActionType.PortOfDischarge ||
              item.action === TransportationTrackingActionType.PortOfLoading) &&
            !isOnlyOneAction
          ) {
            return MapTrackingActionTypeNames.Transshipment;
          }

          return item.action;
        })
        .filter(Boolean),
    );
  }
}
